// ProductPage.js
import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../api/axios";
import { CartContext } from "../context/CartContext";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import RecommendedProducts from "../components/RecommendedProducts";

const ProductPage = () => {
  const { id } = useParams();
  const { addToCart } = useContext(CartContext);
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loadingProduct, setLoadingProduct] = useState(true);

  const [activeTab, setActiveTab] = useState("details");

  // Imagen principal seleccionada
  const [selectedImage, setSelectedImage] = useState(null);

  // Reseñas
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);

  // Formulario reseña
  const [comment, setComment] = useState("");
  const [showName, setShowName] = useState(true);
  const [reviewImage, setReviewImage] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoadingProduct(true);
        const { data } = await API.get(`/products/${id}`);
        setProduct(data);
        setSelectedImage(data.image);
        setLoadingProduct(false);
      } catch (error) {
        console.error("Error obteniendo producto:", error);
        setLoadingProduct(false);
      }
    };
    fetchProduct();
  }, [id]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoadingReviews(true);
        const { data } = await API.get(`/reviews/${id}`);
        setReviews(data);
        setLoadingReviews(false);
      } catch (error) {
        console.error("Error obteniendo reseñas:", error);
        setLoadingReviews(false);
      }
    };
    fetchReviews();
  }, [id]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append("comment", comment);
      formData.append("showName", showName ? "true" : "false");
      if (reviewImage) {
        formData.append("image", reviewImage);
      }
      await API.post(`/reviews/${id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const { data } = await API.get(`/reviews/${id}`);
      setReviews(data);
      setComment("");
      setShowName(true);
      setReviewImage(null);
    } catch (error) {
      console.error("Error creando reseña:", error);
    }
  };

  if (loadingProduct) {
    return <p className="text-center mt-8">Cargando producto...</p>;
  }
  if (!product) {
    return <p className="text-center text-red-500 mt-8">Producto no encontrado.</p>;
  }

  const finalPrice =
    product.discount > 0
      ? (product.price * (1 - product.discount / 100)).toFixed(2)
      : product.price.toFixed(2);

  return (
    <div className="container mx-auto mt-10 px-4 max-w-3xl">
      <div className="flex flex-col md:flex-row gap-6">
        {/* Miniaturas */}
        <div className="w-full md:w-1/4">
          <div className="border rounded p-3">
            <div
              onClick={() => setSelectedImage(product.image)}
              className={`cursor-pointer border p-1 rounded mb-3 ${
                selectedImage === product.image ? "border-blue-500" : "border-gray-300"
              }`}
            >
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/uploads/ProductImage/${product.image}`}
                alt={product.title}
                className="object-contain w-full h-24 rounded"
              />
            </div>
            {product.extraImages &&
              product.extraImages.map((img, i) => (
                <div
                  key={i}
                  onClick={() => setSelectedImage(img)}
                  className={`cursor-pointer border p-1 rounded mb-3 ${
                    selectedImage === img ? "border-blue-500" : "border-gray-300"
                  }`}
                >
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/uploads/ProductImage/${img}`}
                    alt={`extra-${i}`}
                    className="object-contain w-full h-24 rounded"
                  />
                </div>
              ))}
          </div>
        </div>

        {/* Detalles del producto */}
        <div className="w-full md:w-3/4">
          <div className="border rounded p-4 mb-4">
            {/* Cambiamos a object-contain */}
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/uploads/ProductImage/${selectedImage}`}
              alt={product.title}
              className="object-contain w-full h-96 rounded"
            />
          </div>
          <div className="border rounded p-6">
            <h1 className="text-2xl font-bold mb-3">{product.title}</h1>
            {product.discount > 0 ? (
              <p className="text-gray-700 mb-2">
                <span className="line-through text-red-500 mr-2">${product.price}</span>
                <span className="font-bold">Ahora: ${finalPrice}</span>
              </p>
            ) : (
              <p className="text-blue-600 font-bold text-xl mb-2">${finalPrice}</p>
            )}
            <p className="text-gray-600 mb-4">Stock: {product.stock}</p>
            <button
              onClick={() => addToCart(product)}
              disabled={product.stock <= 0}
              className={`w-28 py-1 rounded text-white transition-colors ${
                product.stock > 0 ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400 cursor-not-allowed"
              }`}
            >
              Agregar
            </button>
          </div>
        </div>
      </div>

      {/* Sección de Detalles / Reseñas */}
      <div className="border rounded p-6 mt-6">
        <div className="flex gap-4 border-b pb-2 mb-4">
          <button
            onClick={() => handleTabChange("details")}
            className={`px-4 py-2 ${
              activeTab === "details" ? "font-bold border-b-2 border-black" : "text-gray-600"
            }`}
          >
            Detalle
          </button>
          <button
            onClick={() => handleTabChange("reviews")}
            className={`px-4 py-2 ${
              activeTab === "reviews" ? "font-bold border-b-2 border-black" : "text-gray-600"
            }`}
          >
            Reseñas
          </button>
        </div>

        {activeTab === "details" && (
          <div>
            <h2 className="text-xl font-bold mb-3">Descripción</h2>
            <p className="text-gray-700 leading-relaxed">{product.description}</p>
          </div>
        )}

        {activeTab === "reviews" && (
          <div>
            <h2 className="text-xl font-bold mb-3">Reseñas</h2>
            <form onSubmit={handleReviewSubmit} className="border rounded p-4 mb-4">
              <textarea
                className="w-full border p-2 rounded mb-3"
                rows="3"
                placeholder="Escribe tu reseña..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                required
              />
              <div className="flex items-center mb-3">
                <input
                  type="checkbox"
                  checked={showName}
                  onChange={() => setShowName(!showName)}
                  className="mr-2"
                />
                <label className="text-gray-700">Mostrar mi nombre</label>
              </div>
              <div className="mb-3">
                <label className="block mb-1 text-gray-700">Subir imagen (opcional):</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setReviewImage(e.target.files[0])}
                  className="border p-2 rounded"
                />
              </div>
              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition-colors"
              >
                Enviar
              </button>
            </form>

            {loadingReviews ? (
              <p className="text-center text-gray-600">Cargando reseñas...</p>
            ) : reviews.length === 0 ? (
              <p className="text-center text-gray-600">No hay reseñas todavía.</p>
            ) : (
              reviews.map((rev, i) => (
                <div key={i} className="border rounded p-4 mb-4">
                  <p className="font-bold text-gray-800 mb-1">{rev.userName || "Anónimo"}</p>
                  <p className="text-gray-700 mb-1">{rev.comment}</p>
                  {rev.image && (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/uploads/reviews/${rev.image}`}
                      alt="review-img"
                      className="w-32 h-32 object-cover rounded mb-2"
                    />
                  )}
                  <p className="text-sm text-gray-500">{new Date(rev.createdAt).toLocaleString()}</p>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Sección de productos recomendados */}
      <RecommendedProducts currentProductId={product._id} category={product.category} />
    </div>
  );
};

export default ProductPage;
